/**
 * ContactImportDialog
 * Supports CSV, vCard (.vcf), and Excel (.xlsx / .xls) import.
 * Parses files entirely in the browser, shows a preview, then POSTs
 * the contact array to the existing /contact/import endpoint via the
 * importContacts Redux thunk.
 */
import React, { useState, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Iconify from "../iconify";
import { importContacts } from "../../feature/ContactSlice";

// ─── helpers ────────────────────────────────────────────────────────────────

/** Normalise a raw header string to a known field or 'ignore' */
function guessField(header) {
  const h = (header || "").toLowerCase().replace(/[\s_.,-]/g, "");
  if (/^(first|fname|givenname|given)/.test(h)) return "firstName";
  if (/^(last|lname|surname|family)/.test(h)) return "lastName";
  if (/^(email|mail|e-mail)/.test(h)) return "email";
  if (/^(phone|mobile|cell|tel|fax|number)/.test(h)) return "phoneNumber";
  return "ignore";
}

/** Clean a phone string to digits-only for storage */
function cleanPhone(raw = "") {
  return raw.replace(/\D/g, "");
}

/** Format digits for display */
function displayPhone(digits = "") {
  const d = digits.replace(/\D/g, "");
  const m = d.match(/^(1)?(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[2]}) ${m[3]}-${m[4]}`;
  return digits || "—";
}

// ─── vCard parser ────────────────────────────────────────────────────────────

function parseVcf(text) {
  const contacts = [];
  const cards = text
    .split(/BEGIN:VCARD/i)
    .slice(1)
    .map((c) => "BEGIN:VCARD\n" + c);

  cards.forEach((card) => {
    const lines = card.replace(/\r\n|\r/g, "\n").split("\n");
    const get = (prefix) => {
      const line = lines.find((l) =>
        l.toUpperCase().startsWith(prefix.toUpperCase())
      );
      return line ? line.split(":").slice(1).join(":").trim() : "";
    };

    // N field: Lastname;Firstname;Additional;Prefix;Suffix
    const nField = get("N:");
    let firstName = "";
    let lastName = "";
    if (nField) {
      const parts = nField.split(";");
      lastName = (parts[0] || "").trim();
      firstName = (parts[1] || "").trim();
    }
    // Fallback to FN (full name)
    if (!firstName && !lastName) {
      const fn = get("FN:");
      const parts = fn.trim().split(" ");
      firstName = parts[0] || "";
      lastName = parts.slice(1).join(" ");
    }

    const emailLine = lines.find((l) =>
      l.toUpperCase().includes("EMAIL")
    );
    const email = emailLine
      ? emailLine.split(":").slice(1).join(":").trim()
      : "";

    const telLine = lines.find((l) =>
      l.toUpperCase().startsWith("TEL")
    );
    const phoneNumber = telLine
      ? cleanPhone(telLine.split(":").slice(1).join(":").trim())
      : "";

    if (firstName || lastName || email) {
      contacts.push({ firstName, lastName, email, phoneNumber });
    }
  });

  return contacts;
}

// ─── Styled drop zone ────────────────────────────────────────────────────────

const DropZone = styled(Box)(({ theme, dragover }) => ({
  border: `2px dashed ${
    dragover ? theme.palette.primary.main : "rgba(60,60,67,0.2)"
  }`,
  borderRadius: 12,
  padding: theme.spacing(6, 4),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.2s ease",
  backgroundColor: dragover
    ? "rgba(0,122,255,0.04)"
    : theme.palette.background.default,
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "rgba(0,122,255,0.03)",
  },
}));

// ─── Field mapping row ───────────────────────────────────────────────────────

const CONTACT_FIELDS = [
  { value: "ignore", label: "— ignore —" },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "phoneNumber", label: "Phone Number" },
];

// ─── Main component ──────────────────────────────────────────────────────────

const STEPS = ["Choose file", "Map columns", "Preview & import"];

export default function ContactImportDialog({ open, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const fileRef = useRef(null);

  const [step, setStep] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileType, setFileType] = useState(""); // "csv" | "vcf" | "xlsx"
  const [rawHeaders, setRawHeaders] = useState([]);   // CSV/Excel column names
  const [fieldMap, setFieldMap] = useState({});       // { colName: contactField }
  const [rawRows, setRawRows] = useState([]);         // CSV/Excel raw rows (array of objects)
  const [preview, setPreview] = useState([]);         // mapped Contact[]
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { count } | null
  const [error, setError] = useState("");

  // ── reset ──────────────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    setStep(0);
    setDragOver(false);
    setFileName("");
    setFileType("");
    setRawHeaders([]);
    setFieldMap({});
    setRawRows([]);
    setPreview([]);
    setImporting(false);
    setImportResult(null);
    setError("");
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  // ── file ingestion ─────────────────────────────────────────────────────────
  const processFile = useCallback((file) => {
    if (!file) return;
    setError("");
    const name = file.name;
    setFileName(name);
    const ext = name.split(".").pop().toLowerCase();

    if (ext === "vcf") {
      setFileType("vcf");
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const contacts = parseVcf(e.target.result);
          if (!contacts.length) {
            setError("No contacts found in the vCard file.");
            return;
          }
          setPreview(contacts);
          setStep(2); // skip column-mapping for vCard
        } catch {
          setError("Failed to parse vCard file.");
        }
      };
      reader.readAsText(file);
      return;
    }

    if (ext === "csv") {
      setFileType("csv");
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const headers = results.meta.fields || [];
          if (!headers.length) {
            setError("CSV has no headers.");
            return;
          }
          const initialMap = {};
          headers.forEach((h) => { initialMap[h] = guessField(h); });
          setRawHeaders(headers);
          setFieldMap(initialMap);
          setRawRows(results.data);
          setStep(1);
        },
        error: () => setError("Failed to parse CSV."),
      });
      return;
    }

    if (ext === "xlsx" || ext === "xls") {
      setFileType("xlsx");
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "binary" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
          if (!data.length) {
            setError("Spreadsheet is empty.");
            return;
          }
          const headers = Object.keys(data[0]);
          const initialMap = {};
          headers.forEach((h) => { initialMap[h] = guessField(h); });
          setRawHeaders(headers);
          setFieldMap(initialMap);
          setRawRows(data);
          setStep(1);
        } catch {
          setError("Failed to read Excel file.");
        }
      };
      reader.readAsBinaryString(file);
      return;
    }

    setError("Unsupported file type. Please use .csv, .vcf, .xlsx, or .xls");
  }, []);

  const handleFilePick = (e) => {
    processFile(e.target.files[0]);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files[0]);
  };

  // ── column mapping → preview ───────────────────────────────────────────────
  const buildPreview = useCallback(() => {
    const mapped = rawRows.map((row) => {
      const contact = { firstName: "", lastName: "", email: "", phoneNumber: "" };
      Object.entries(fieldMap).forEach(([col, field]) => {
        if (field !== "ignore") {
          const val = (row[col] || "").toString().trim();
          if (field === "phoneNumber") {
            contact.phoneNumber = cleanPhone(val);
          } else {
            contact[field] = val;
          }
        }
      });
      return contact;
    });
    // Filter out rows with no meaningful data
    const valid = mapped.filter(
      (c) => c.firstName || c.lastName || c.email
    );
    setPreview(valid);
    setStep(2);
  }, [rawRows, fieldMap]);

  // ── import ─────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    setImporting(true);
    setError("");
    try {
      const result = await dispatch(importContacts(preview));
      if (result.error) {
        setError("Import failed. The server rejected one or more contacts.");
      } else {
        setImportResult({ count: preview.length });
        onSuccess && onSuccess(preview.length);
      }
    } catch {
      setError("Unexpected error during import.");
    } finally {
      setImporting(false);
    }
  };

  // ─── render ────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={importing ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #007AFF 0%, #0066CC 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 2,
        }}
      >
        <Iconify icon="eva:upload-fill" sx={{ width: 22, height: 22 }} />
        Import Contacts
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Stepper */}
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* ── Step 0: file picker ── */}
        {step === 0 && (
          <>
            <DropZone
              dragover={dragOver ? 1 : 0}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <Iconify
                icon="eva:cloud-upload-fill"
                sx={{ width: 48, height: 48, color: "primary.main", mb: 1.5 }}
              />
              <Typography variant="h6" gutterBottom>
                Drop your file here, or{" "}
                <Typography
                  component="span"
                  variant="h6"
                  color="primary"
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  browse
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: <strong>.csv</strong>&nbsp;&nbsp;
                <strong>.vcf</strong>&nbsp;&nbsp;<strong>.xlsx</strong>&nbsp;&nbsp;
                <strong>.xls</strong>
              </Typography>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.vcf,.xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFilePick}
              />
            </DropZone>

            {/* Format tips */}
            <Stack direction="row" spacing={1.5} mt={2.5} flexWrap="wrap">
              {[
                {
                  icon: "vscode-icons:file-type-excel",
                  label: "Excel / CSV",
                  tip: "Export from Outlook, Google Contacts, or any spreadsheet. First row must be headers.",
                },
                {
                  icon: "eva:phone-fill",
                  label: "vCard (.vcf)",
                  tip: "Export from iPhone Contacts, Mac Contacts, or Outlook.",
                },
              ].map(({ icon, label, tip }) => (
                <Box
                  key={label}
                  sx={{
                    p: 1.5,
                    border: "1px solid rgba(60,60,67,0.12)",
                    borderRadius: 2,
                    flex: "1 1 200px",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                    <Iconify icon={icon} sx={{ width: 18, height: 18 }} />
                    <Typography variant="subtitle2">{label}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {tip}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* ── Step 1: column mapping (CSV / Excel only) ── */}
        {step === 1 && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              We auto-detected column mappings from <strong>{fileName}</strong>.
              Adjust any that look wrong before previewing.
            </Alert>
            <TableContainer
              sx={{
                border: "1px solid rgba(60,60,67,0.10)",
                borderRadius: 2,
                maxHeight: 340,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Column in file</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sample value</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Map to field</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rawHeaders.map((col) => (
                    <TableRow key={col} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {col}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 180,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {(rawRows[0]?.[col] || "").toString() || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={fieldMap[col] || "ignore"}
                            onChange={(e) =>
                              setFieldMap((prev) => ({
                                ...prev,
                                [col]: e.target.value,
                              }))
                            }
                          >
                            {CONTACT_FIELDS.map((f) => (
                              <MenuItem key={f.value} value={f.value}>
                                {f.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* ── Step 2: preview ── */}
        {step === 2 && !importResult && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>{preview.length} contacts</strong> ready to import from{" "}
              <em>{fileName}</em>. Review below then click Import.
            </Alert>
            <TableContainer
              sx={{
                border: "1px solid rgba(60,60,67,0.10)",
                borderRadius: 2,
                maxHeight: 340,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["First Name", "Last Name", "Email", "Phone"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700 }}>
                        {h}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((c, i) => {
                    const hasEmail = !!c.email;
                    const warn = !hasEmail;
                    return (
                      <TableRow key={i} hover>
                        <TableCell>{c.firstName || "—"}</TableCell>
                        <TableCell>{c.lastName || "—"}</TableCell>
                        <TableCell>{c.email || "—"}</TableCell>
                        <TableCell>
                          {c.phoneNumber ? displayPhone(c.phoneNumber) : "—"}
                        </TableCell>
                        <TableCell>
                          {warn ? (
                            <Chip
                              label="No email"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Ready"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {importing && (
              <Box mt={2}>
                <LinearProgress />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Importing contacts…
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* ── Import result ── */}
        {importResult && (
          <Box textAlign="center" py={4}>
            <Iconify
              icon="eva:checkmark-circle-2-fill"
              sx={{ width: 64, height: 64, color: "success.main", mb: 2 }}
            />
            <Typography variant="h5" gutterBottom>
              Import complete!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <strong>{importResult.count}</strong> contacts have been added
              to your contacts list.
            </Typography>
          </Box>
        )}

        {/* Error banner */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {importResult ? (
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{ borderRadius: 20 }}
          >
            Done
          </Button>
        ) : (
          <>
            {step > 0 && !importing && (
              <Button
                onClick={() => {
                  if (step === 2 && fileType !== "vcf") setStep(1);
                  else { reset(); }
                }}
                sx={{ borderRadius: 20 }}
              >
                Back
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={handleClose}
              disabled={importing}
              sx={{ borderRadius: 20 }}
            >
              Cancel
            </Button>
            {step === 1 && (
              <Button
                onClick={buildPreview}
                variant="contained"
                sx={{ borderRadius: 20 }}
              >
                Preview →
              </Button>
            )}
            {step === 2 && !importing && (
              <Button
                onClick={handleImport}
                variant="contained"
                disabled={!preview.length || importing}
                startIcon={<Iconify icon="eva:upload-fill" />}
                sx={{ borderRadius: 20 }}
              >
                Import {preview.length} contacts
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
